(() => {
  if (customElements.get('sp-product-samples')) return;
  const normalize = value => String(value || '').normalize('NFKC').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const escape = value => String(value || '').replace(/[&<>"']/g, character => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[character]));
  class ProductSamples extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.status = this.querySelector('[data-sp-sample-status]');
      this.items = this.querySelector('[data-sp-sample-items]');
      this.options = this.querySelector('[data-sp-sample-options]');
      this.checkout = this.querySelector('[data-sp-sample-checkout]');
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      const source = JSON.parse(this.querySelector('[data-sp-sample-catalog]').textContent);
      const seen = new Set();
      this.catalog = source.catalog.filter(item => Number(item.price) === 0).sort((a,b) => Number(a.handle.startsWith('sofa-'))-Number(b.handle.startsWith('sofa-'))).filter(item => {
        const key = normalize(item.title);
        if (seen.has(key)) return false;
        seen.add(key); return true;
      });
      this.byId = new Map(this.catalog.map(item => [String(item.id), item]));
      this.current = this.catalog.find(item => normalize(item.title) === normalize(source.current));
      this.cart = null;
      this.busy = false;
      this.family = 'All';
      this.currency = this.dataset.currency || 'USD';
      const families = ['All', ...new Set(this.catalog.map(item => item.family))];
      this.querySelector('[data-sp-sample-filters]').innerHTML = families.map(family => `<button type="button" data-sp-sample-family="${escape(family)}" aria-pressed="${family==='All'}">${escape(family)}</button>`).join('');
      this.openHandler = event => {
        const trigger = event.target.closest('[data-sp-samples-open]');
        if (!trigger || trigger.closest('section')?.id !== `MainProduct-${this.dataset.sectionId}`) return;
        event.preventDefault(); this.open(trigger);
      };
      document.addEventListener('click', this.openHandler);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-sp-sample-close]')) this.dialog.close();
        const add = event.target.closest('[data-sp-sample-add]');
        if (add && !this.busy) this.addSample(add.dataset.spSampleAdd);
        const remove = event.target.closest('[data-sp-sample-remove]');
        if (remove && !this.busy) this.removeSample(remove.dataset.spSampleRemove);
        const family = event.target.closest('[data-sp-sample-family]');
        if (family) {this.family=family.dataset.spSampleFamily;this.renderOptions();}
      });
      this.querySelector('[data-sp-sample-search]').addEventListener('input', () => this.renderOptions());
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog && event.clientX < this.dialog.getBoundingClientRect().left) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('sp-samples-lock');
        this.opener?.focus();
      });
      this.checkout.addEventListener('click', () => {
        if (!this.busy && this.sampleLines().length) window.location.assign(this.root + 'checkout');
      });
      this.cartHandler = event => {if (event.detail?.cart?.items && !this.busy) {this.cart=event.detail.cart;this.render();}};
      document.addEventListener('cart:change', this.cartHandler);
      this.renderOptions();
      document.querySelectorAll(`#MainProduct-${CSS.escape(this.dataset.sectionId)} [data-sp-samples-open]`).forEach(button => {button.disabled=false;button.setAttribute('aria-busy','false');});
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.openHandler);
      document.removeEventListener('cart:change', this.cartHandler);
      if (this.dialog?.open) this.dialog.close();
    }
    money(cents) {
      try {return new Intl.NumberFormat(document.documentElement.lang || 'en-US',{style:'currency',currency:this.cart?.currency || this.currency}).format(Number(cents)/100);}
      catch {return `${(Number(cents)/100).toFixed(2)} ${this.currency}`;}
    }
    sampleLines() {return (this.cart?.items || []).filter(item => String(item.properties?._fabric_sample)==='true' || this.byId.has(String(item.variant_id)));}
    sampleCount() {return this.sampleLines().reduce((sum,item) => sum+Number(item.quantity),0);}
    message(text, error=false) {this.status.textContent=text;this.status.classList.toggle('is-error',error);}
    setBusy(value) {
      this.busy=value;this.dialog.setAttribute('aria-busy',String(value));
      this.checkout.disabled=value || !this.sampleLines().length;
      this.querySelectorAll('[data-sp-sample-add],[data-sp-sample-remove]').forEach(button => {button.disabled=value || (button.hasAttribute('data-sp-sample-add') && (button.getAttribute('aria-pressed')==='true' || this.sampleCount()>=5));});
    }
    async request(path, data) {
      const response = await fetch(this.root+path,{method:data?'POST':'GET',headers:{Accept:'application/json',...(data?{'Content-Type':'application/json'}:{})},...(data?{body:JSON.stringify(data)}:{})});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to update your samples. Please try again.');
      return result;
    }
    async open(trigger) {
      if (this.dialog.open) return;
      this.opener=trigger;this.dialog.showModal();document.body.classList.add('sp-samples-lock');
      this.querySelector('[data-sp-sample-close]').focus();
      this.setBusy(true);this.message('Loading your samples…');
      try {
        this.cart=await this.request('cart.js');this.render();
        const alreadySelected=this.current && this.sampleLines().some(item => String(item.variant_id)===String(this.current.id));
        if (this.current && !alreadySelected && this.sampleCount()<5) {
          await this.performAdd(this.current.id);
          this.message(`${this.current.title} added to your sample bag.`);
        } else {
          this.message(this.sampleCount()>=5?'You have 5 samples. Remove one to choose another.':'');
        }
        if (!this.sampleLines().length) this.querySelector('[data-sp-sample-chooser]').open=true;
      } catch(error) {this.message(error.message,true);}
      finally {this.setBusy(false);}
    }
    async refresh() {
      this.cart=await this.request('cart.js');
      document.querySelector('cart-drawer')?.classList.toggle('is-empty',!this.cart.item_count);
      this.render();
      document.dispatchEvent(new CustomEvent('cart:change',{detail:{cart:this.cart,source:'sp-product-samples'}}));
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], [data-cart-count]').forEach(node => {node.textContent=String(this.cart.item_count || 0);});
      document.querySelectorAll('.cart-count-bubble .visually-hidden').forEach(node => {node.textContent=`${this.cart.item_count || 0} items`;});
    }
    async performAdd(id) {
      const latest=await this.request('cart.js');this.cart=latest;
      if (!this.byId.has(String(id))) throw new Error('This free sample is unavailable. Please choose another.');
      if (this.sampleLines().some(item => String(item.variant_id)===String(id))) {this.render();return;}
      if (this.sampleCount()>=5) throw new Error('You can choose up to 5 free samples. Remove one before adding another.');
      const drawer=document.querySelector('cart-drawer');
      const payload={items:[{id:Number(id),quantity:1,properties:{_fabric_sample:'true'}}]};
      if (drawer?.getSectionsToRender) {payload.sections=drawer.getSectionsToRender().map(section => section.id);payload.sections_url=location.pathname;}
      const added=await this.request('cart/add.js',payload);
      if (added.sections) this.updateCartMarkup(added.sections);
      await this.refresh();
    }
    async addSample(id) {
      this.setBusy(true);this.message('Adding sample…');
      try {await this.performAdd(id);this.message(`${this.byId.get(String(id)).title} added to your sample bag.`);}
      catch(error) {this.message(error.message,true);}
      finally {this.setBusy(false);}
    }
    async removeSample(key) {
      this.setBusy(true);this.message('Removing sample…');
      try {
        const payload={id:key,quantity:0,sections:['cart-drawer','cart-icon-bubble'],sections_url:location.pathname};
        const changed=await this.request('cart/change.js',payload);
        if (changed.sections) this.updateCartMarkup(changed.sections);
        await this.refresh();this.message('Sample removed.');
        (this.querySelector('[data-sp-sample-remove]') || this.querySelector('[data-sp-sample-chooser] summary'))?.focus();
      } catch(error) {this.message(error.message,true);}
      finally {this.setBusy(false);}
    }
    updateCartMarkup(sections) {
      const parser=new DOMParser();
      const drawerHtml=sections['cart-drawer'];
      if (drawerHtml) {
        const updated=parser.parseFromString(drawerHtml,'text/html').querySelector('#CartDrawer .drawer__inner');
        const current=document.querySelector('cart-drawer #CartDrawer .drawer__inner');
        if (updated && current) {current.innerHTML=updated.innerHTML;current.className=updated.className;}
      }
      const bubbleHtml=sections['cart-icon-bubble'];
      if (bubbleHtml) {
        const updated=parser.parseFromString(bubbleHtml,'text/html').querySelector('#cart-icon-bubble');
        const current=document.querySelector('#cart-icon-bubble');
        if (updated && current) current.innerHTML=updated.innerHTML;
      }
    }
    render() {
      const lines=this.sampleLines();const count=this.sampleCount();
      this.querySelector('[data-sp-sample-count]').textContent=`${count} / 5`;
      this.querySelector('[data-sp-sample-total]').textContent=this.money(lines.reduce((sum,item) => sum+Number(item.final_line_price),0));
      let index=0;
      this.items.innerHTML=lines.length?lines.map(item => {
        const catalog=this.byId.get(String(item.variant_id));
        const title=item.product_title || catalog?.title || item.title;
        const image=catalog?.image || item.image;
        index+=Number(item.quantity);
        return `<article class="sp-samples__item">${image?`<img src="${escape(image)}" alt="${escape(title)}" width="100" height="104">`:'<div class="sp-samples__item-placeholder" aria-hidden="true"></div>'}<div class="sp-samples__item-info"><h3>${escape(title)}</h3><button type="button" data-sp-sample-remove="${escape(item.key)}" aria-label="Remove ${escape(title)}">Remove</button><div class="sp-samples__item-meta"><span>${this.money(item.final_line_price)}</span><span>${Number(item.quantity)>1?`Qty ${Number(item.quantity)} · `:''}Sample ${index} / 5</span></div></div></article>`;
      }).join(''):'<p class="sp-samples__empty">Your sample bag is empty. Choose your favourite fabrics below.</p>';
      this.renderOptions();this.setBusy(this.busy);
    }
    renderOptions() {
      const keyword=normalize(this.querySelector('[data-sp-sample-search]').value);
      const selected=new Set(this.sampleLines().map(item => String(item.variant_id)));
      this.querySelectorAll('[data-sp-sample-family]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.spSampleFamily===this.family)));
      const matches=this.catalog.filter(item => (this.family==='All'||this.family===item.family) && normalize(item.title).includes(keyword));
      this.options.innerHTML=matches.length?matches.map(item => `<button type="button" class="sp-samples__option" data-sp-sample-add="${escape(item.id)}" aria-pressed="${selected.has(String(item.id))}" ${this.busy||selected.has(String(item.id))||this.sampleCount()>=5?'disabled':''}>${item.image?`<img src="${escape(item.image)}" alt="" width="120" height="120" loading="lazy">`:''}<span>${escape(item.title)}</span><b>${selected.has(String(item.id))?'✓ Added':'+ Add sample'}</b></button>`).join(''):'<p>No samples match your search.</p>';
    }
  }
  customElements.define('sp-product-samples',ProductSamples);
})();
